// Private

import { FieldType, READ_ONLY_WIDGET_FIELDS } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import isPlainObject from 'lodash/isPlainObject';
import { isNullOrUndefined } from './helpers';

// const isConfigurationResponded = (configuration) => {
// 	return !!configuration.user_response;
// };

const configurationFieldIsCredentialsSelector = (field) => field.field_type === FieldType.CredentialsSelector;
const configurationFieldIsOutputField = (field) => field.field_type === FieldType.ParagraphOutput;

const configurationFieldIsCredentialsSelectorAndBelongsToService = (field, serviceId, serviceVersionId) =>
	configurationFieldIsCredentialsSelector(field) &&
	field.metadata.source.service === serviceId &&
	field.metadata.source.service_version === serviceVersionId;

const allConfigurationFieldsAnswered = (configuration) => {
	// TODO - filter out the not needed to be answered fields (TextOutput/Paragraph, for example)
	const allFieldsPerInterface = getRequiredConfigurationFieldsIdsPerInterface(configuration);
	const fieldsResponses = configuration.user_response?.fields_responses || [];
	return Object.values(allFieldsPerInterface).every((configurationInterfaceFieldsUUIDs) =>
		configurationInterfaceFieldsUUIDs.every(
			(configurationInterfaceFieldUUID) => !isNullOrUndefined(fieldsResponses[configurationInterfaceFieldUUID])
		)
	);
};

// Public
const getConfigurationFieldsPerInterface = (configuration, options = {}) => {
	const skipNonInsertDataResponseFields = options?.skipNonInsertDataResponseFields || false;
	const fieldType = options?.fieldType;

	const fieldsPerInterface = {};
	configuration?.workflow_configuration_interfaces.forEach(({ interface: configInterface }) => {
		let fieldsToAdd = configInterface.fields;
		if (skipNonInsertDataResponseFields) {
			fieldsToAdd = fieldsToAdd.filter(
				(field) => !configurationFieldIsCredentialsSelector(field) && !configurationFieldIsOutputField(field)
			);
		}
		if (fieldType) {
			fieldsToAdd = fieldsToAdd.filter((field) => field.field_type === fieldType);
		}
		fieldsPerInterface[configInterface.id] = fieldsToAdd;
	});
	return fieldsPerInterface;
};

const getRequiredConfigurationFieldsIdsPerInterface = (configuration, options = {}) => {
	const fieldsPerInterface = getConfigurationFieldsPerInterface(configuration, options);
	return Object.fromEntries(
		Object.entries(fieldsPerInterface).map(([interfaceUUID, interfaceFields]) => [
			interfaceUUID,
			interfaceFields.filter((field) => !READ_ONLY_WIDGET_FIELDS.includes(field.field_type)).map((field) => field.id),
		])
	);
};

const getConfigurationFieldsIdsPerInterface = (configuration, options = {}) => {
	const fieldsPerInterface = getConfigurationFieldsPerInterface(configuration, options);
	return Object.fromEntries(
		Object.entries(fieldsPerInterface).map(([interfaceUUID, interfaceFields]) => [interfaceUUID, interfaceFields.map((field) => field.id)])
	);
};

const getConfigurationFieldsIds = (configuration, options = {}) => {
	const fieldsPerInterface = getConfigurationFieldsPerInterface(configuration, options);
	return Object.values(fieldsPerInterface)
		.map((fieldsList) => fieldsList.map((interfaceFields) => interfaceFields.id))
		.flat(1); // from array of arrays with fieldsIds, to array with fieldsIds // [[1,2,3], [4,5,6]] => [1,2,3,4,5,6]
};

const getConfigurationMappedFields = (configuration, getConfigurationFieldResponse) => {
	const configurationsMappedFields = {};

	if (!configuration) {
		return;
	}
	const fieldsList = getConfigurationFieldsIds(configuration, { skipNonInsertDataResponseFields: true });
	const mapperFields = getUserSchemaMapperFields(configuration);
	fieldsList.forEach(
		(fieldUUID) =>
			(configurationsMappedFields[fieldUUID] =
				getMappedFieldsConfigurationFieldResponse(fieldUUID, getConfigurationFieldResponse, mapperFields) || 'Sample')
	);
	return configurationsMappedFields;
};

const getUserSchemaMapperFields = (configuration) => {
	const mapperFields = [];
	configuration?.workflow_configuration_interfaces.forEach(({ interface: configInterface }) => {
		mapperFields.push(...configInterface.fields.filter((field) => field.field_type === FieldType.UserSchemaMappingsSelector));
	});

	return mapperFields;
};

const getMappedFieldsConfigurationFieldResponse = (fieldUUID, getConfigurationFieldResponse, mapperFields) => {
	const response = getConfigurationFieldResponse(fieldUUID) || 'Sample';
	if (isPlainObject(response)) {
		const maybeMapperField = mapperFields.find((field) => field.id === fieldUUID);
		if (maybeMapperField && response?.params_map) {
			return { params_map: response.params_map };
		}
	}

	return response;
};

const getReplaceInsertDataStringDictionary = (generalConfiguration, userConfiguration) => ({
	...getReplaceInsertDataStringDictionaryForConfiguration(generalConfiguration),
	...getReplaceInsertDataStringDictionaryForConfiguration(userConfiguration),
});

const getReplaceInsertDataStringDictionaryForConfiguration = (configuration) => {
	let dict = {};
	configuration?.workflow_configuration_interfaces.forEach((interfaceInfo) => {
		interfaceInfo?.interface?.fields
			.filter((f) => f.field_type === FieldType.UserSchemaMappingsSelector)
			.forEach((field) => {
				if (field?.metadata?.source?.mappings?.length > 0) {
					dict = {
						...dict,
						...Object.fromEntries(field.metadata?.source?.mappings.map((m) => [`${field.id}.${m.originalKey}`, m.originalKey])),
					};
				}
			});
	});
	return dict;
};

const isConfigurationRespondedSuccessfully = (configuration) => allConfigurationFieldsAnswered(configuration);

const getConfigurationInterfaceFromField = (configurationId, fieldUUID, getConfigurationFieldsIdsPerInterface) => {
	const configurationFields = getConfigurationFieldsIdsPerInterface(configurationId);
	return Object.entries(configurationFields).find(([, interfaceFields]) => interfaceFields.includes(fieldUUID))?.[0];
};

const getConfigurationCredentialsSelectorFieldsForService = (configurationId, serviceId, serviceVersion, getConfiguration) => {
	const configuration = getConfiguration(configurationId);
	const configurationFieldsPerInterface = getConfigurationFieldsPerInterface(configuration);
	const credentialsConfigurationsField = [];
	Object.values(configurationFieldsPerInterface).forEach((configurationFields) => {
		credentialsConfigurationsField.push(
			...configurationFields.filter((configurationField) =>
				configurationFieldIsCredentialsSelectorAndBelongsToService(configurationField, serviceId, serviceVersion)
			)
		);
	});
	return credentialsConfigurationsField;
};

const WorkflowConfigurationService = {
	getConfigurationFieldsPerInterface,
	getConfigurationMappedFields,
	isConfigurationRespondedSuccessfully,
	getConfigurationInterfaceFromField,
	getConfigurationCredentialsSelectorFieldsForService,
	getConfigurationFieldsIdsPerInterface,
	getReplaceInsertDataStringDictionary,
	getConfigurationFieldsIds,
};

export default WorkflowConfigurationService;
